import { Controller } from "@hotwired/stimulus"

/**
 * Controls the neighbor application side panel:
 * - Search: client-side filter + <mark> highlight on question titles and answer text
 * - "Hide empty responses" checkbox toggles visibility of empty rows
 * - "Expand all" / "Collapse all" toggles all <details> sections
 */
export default class extends Controller {
  static targets = ["hideEmpty", "expandBtn", "container", "search"]

  declare readonly hideEmptyTarget: HTMLInputElement
  declare readonly expandBtnTarget: HTMLElement
  declare readonly containerTarget: HTMLElement
  declare readonly searchTarget: HTMLInputElement

  private allExpanded = false
  private searchTimeout: ReturnType<typeof setTimeout> | undefined

  connect() {
    this.toggleEmpty()
  }

  disconnect() {
    clearTimeout(this.searchTimeout)
  }

  search() {
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => this.performSearch(), 50)
  }

  private performSearch() {
    const query = this.searchTarget.value.trim().toLowerCase()
    const sections = this.containerTarget.querySelectorAll<HTMLElement>(".neighbor-section")

    sections.forEach((section) => {
      const rows = section.querySelectorAll<HTMLElement>(".neighbor-response-row")
      let sectionHasMatch = false

      rows.forEach((row) => {
        const questionEl = row.querySelector<HTMLElement>(".neighbor-question-text")
        const answerEl = row.querySelector<HTMLElement>(".neighbor-answer-text")

        const questionOriginal = questionEl?.dataset.original || ""
        const answerOriginal = answerEl?.dataset.original || ""

        if (!query) {
          // No search — restore original text, show all
          if (questionEl) questionEl.innerHTML = questionOriginal
          if (answerEl) answerEl.innerHTML = answerOriginal
          row.classList.toggle(
            "hidden",
            this.hideEmptyTarget.checked && row.dataset.empty === "true"
          )
          sectionHasMatch = true
          return
        }

        const questionMatch = questionOriginal.toLowerCase().includes(query)
        const answerMatch = answerOriginal.toLowerCase().includes(query)

        if (questionMatch || answerMatch) {
          row.classList.remove("hidden")
          sectionHasMatch = true
          if (questionEl) questionEl.innerHTML = this.highlightText(questionOriginal, query)
          if (answerEl) answerEl.innerHTML = this.highlightText(answerOriginal, query)
        } else {
          row.classList.add("hidden")
          // Restore originals on hidden rows so they're clean when revealed
          if (questionEl) questionEl.innerHTML = questionOriginal
          if (answerEl) answerEl.innerHTML = answerOriginal
        }
      })

      if (query) {
        section.classList.toggle("hidden", !sectionHasMatch)
        if (sectionHasMatch) (section as HTMLDetailsElement).open = true
      } else {
        section.classList.remove("hidden")
      }
    })
  }

  private highlightText(text: string, query: string): string {
    if (!query) return this.escapeHtml(text)

    const escaped = this.escapeHtml(text)
    const escapedQuery = this.escapeHtml(query)
    const regex = new RegExp(`(${escapedQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi")
    return escaped.replace(regex, "<mark>$1</mark>")
  }

  private escapeHtml(text: string): string {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  toggleEmpty() {
    const hide = this.hideEmptyTarget.checked
    this.containerTarget.querySelectorAll<HTMLElement>(".neighbor-response-row").forEach((row) => {
      if (row.dataset.empty === "true") {
        row.classList.toggle("hidden", hide)
      }
    })
  }

  expandAll() {
    this.allExpanded = !this.allExpanded
    this.containerTarget.querySelectorAll("details").forEach((d) => {
      d.open = this.allExpanded
    })
    this.expandBtnTarget.textContent = this.allExpanded ? "Collapse all" : "Expand all"
  }
}
